import { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { 
  Megaphone, 
  ExternalLink, 
  ChevronRight, 
  ChevronLeft,
  AlertTriangle,
  Phone
} from "lucide-react";
import axios from "axios";

const API = `${window.location.origin}/api`;

export const AdsSection = () => {
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAds();
  }, []);

  // Auto-rotate ads every 5 seconds
  useEffect(() => {
    if (ads.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % ads.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [ads.length]);

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/ads/active`);
      setAds(response.data);
    } catch (error) {
      console.error("Error fetching ads:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdClick = async (ad) => {
    // تسجيل النقرة
    try {
      await axios.post(`${API}/ads/${ad.id}/click`);
    } catch (error) {
      // ignore
    }

    // فتح الرابط إذا موجود
    if (ad.link) {
      window.open(ad.link, "_blank");
    }
  };

  const nextAd = () => {
    setCurrentIndex((prev) => (prev + 1) % ads.length);
  };

  const prevAd = () => {
    setCurrentIndex((prev) => (prev - 1 + ads.length) % ads.length);
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
        <CardContent className="p-4">
          <div className="h-24 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (ads.length === 0) {
    return null; // لا تظهر شيء إذا لا توجد إعلانات
  }

  const currentAd = ads[currentIndex];

  return (
    <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30 overflow-hidden">
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-amber-500/20 border-b border-amber-500/30">
          <div className="flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-amber-500" />
            <span className="text-amber-400 text-sm font-medium">عروض وإعلانات</span>
          </div>
          {ads.length > 1 && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={prevAd}
                className="h-6 w-6 text-amber-400 hover:text-amber-300 hover:bg-amber-500/20"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <span className="text-amber-400 text-xs">
                {currentIndex + 1} / {ads.length}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={nextAd}
                className="h-6 w-6 text-amber-400 hover:text-amber-300 hover:bg-amber-500/20"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Ad Content */}
        <div 
          className="p-4 cursor-pointer hover:bg-amber-500/5 transition-colors"
          onClick={() => handleAdClick(currentAd)}
        >
          {currentAd.ad_type === "banner" ? (
            // Banner Ad
            <div className="relative">
              <img
                src={currentAd.content}
                alt={currentAd.title}
                className="w-full h-32 object-cover rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {currentAd.link && (
                <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  زيارة
                </div>
              )}
            </div>
          ) : (
            // Text Ad
            <div className="space-y-2">
              <h3 className="text-white font-bold text-lg">{currentAd.title}</h3>
              <p className="text-slate-300 text-sm leading-relaxed">{currentAd.content}</p>
              {currentAd.link && (
                <div className="flex items-center gap-1 text-amber-400 text-sm">
                  <ExternalLink className="w-4 h-4" />
                  <span>اضغط للمزيد</span>
                </div>
              )}
            </div>
          )}

          {/* Advertiser Info */}
          <div className="mt-3 pt-3 border-t border-amber-500/20 flex items-center justify-between">
            <span className="text-slate-400 text-xs">
              بواسطة: {currentAd.advertiser_name}
            </span>
            {currentAd.advertiser_phone && (
              <a
                href={`tel:${currentAd.advertiser_phone}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-emerald-400 text-xs hover:text-emerald-300"
              >
                <Phone className="w-3 h-3" />
                {currentAd.advertiser_phone}
              </a>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="px-4 py-2 bg-slate-800/50 border-t border-amber-500/20">
          <p className="text-slate-500 text-[10px] flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            المحتوى الإعلاني من مسؤولية المُعلن. نازاريو حساباتي غير مسؤول عن المنتجات أو الخدمات المعروضة.
          </p>
        </div>

        {/* Dots Indicator */}
        {ads.length > 1 && (
          <div className="flex justify-center gap-1 py-2 bg-slate-800/30">
            {ads.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex ? "bg-amber-500" : "bg-slate-600"
                }`}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
